import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  OnboardingPlan,
  GetPlansResponse,
  CreateCheckoutRequest,
  CreateCheckoutResponse,
} from '@dreamai/types';
import api from '../../../api';
import { ReactComponent as CloseIcon } from '../../../icons/close-modal.svg';
import { PLAN_TAGLINES, getFeatureList } from './planFeatures';
import './plan-selection-modal.scss';

interface PlanSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPriceId?: string;
}

/** Format a price amount with currency symbol (includes 20% VAT) */
function formatPrice(amount: number, currency: string): string {
  const symbols: Record<string, string> = { GBP: '£', USD: '$', EUR: '€' };
  const sym = symbols[currency] || currency + ' ';
  const withTax = Math.round(amount * 1.2 * 100) / 100;
  const display = withTax.toFixed(2).replace(/\.00$/, '');
  return `${sym}${display}`;
}

const CheckIcon: FC = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M5 13l4 4L19 7' />
  </svg>
);

const ArrowRightIcon: FC = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <line x1='4' y1='12' x2='20' y2='12' />
    <polyline points='14 6 20 12 14 18' />
  </svg>
);

const PlanSelectionModal: FC<PlanSelectionModalProps> = ({
  isOpen,
  onClose,
  currentPriceId,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [plans, setPlans] = useState<OnboardingPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [submittingPlanId, setSubmittingPlanId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currency, setCurrency] = useState<'GBP' | 'USD' | null>(null);
  const [activePlanIdx, setActivePlanIdx] = useState(0);
  const activePlanTabRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoadingPlans(true);
    setError(null);

    const fetchPlans = async () => {
      try {
        const token = await getAccessTokenSilently();
        const url = currency
          ? `/onboarding/plans?currency=${currency}`
          : '/onboarding/plans';
        const res = await api.get<GetPlansResponse>(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success && res.data.plans.length > 0) {
          setPlans(res.data.plans);
          if (!currency) {
            const cur = res.data.plans[0].currency?.toUpperCase();
            if (cur === 'GBP' || cur === 'USD') setCurrency(cur);
          }
        }
      } catch (err) {
        console.error('Error fetching plans:', err);
        setError('Failed to load plans. Please try again.');
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, [isOpen, getAccessTokenSilently, currency]);

  useEffect(() => {
    activePlanTabRef.current?.scrollIntoView({
      inline: 'nearest',
      block: 'nearest',
    });
  }, [activePlanIdx]);

  const handleChoosePlan = useCallback(
    async (plan: OnboardingPlan) => {
      setSubmittingPlanId(plan.priceId);
      setError(null);
      try {
        const token = await getAccessTokenSilently();
        const body: CreateCheckoutRequest = {
          priceId: plan.priceId,
          returnUrl: window.location.origin,
        };

        const response = await api.post<CreateCheckoutResponse>(
          '/onboarding/create-checkout',
          body,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        window.location.href = response.data.checkoutUrl;
      } catch (err) {
        const axiosErr = err as { response?: { data?: { error?: string } } };
        setError(
          axiosErr.response?.data?.error ||
            'Failed to create checkout. Please try again.',
        );
        setSubmittingPlanId(null);
      }
    },
    [getAccessTokenSilently],
  );

  if (!isOpen) return null;

  return (
    <div className='plan-selection-modal-overlay' onClick={onClose}>
      <div
        className='plan-selection-modal'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='modal-header'>
          <h2>Select a plan</h2>
          <div className='currency-toggle'>
            <button
              className={currency === 'GBP' ? 'selected' : ''}
              onClick={() => setCurrency('GBP')}
              disabled={loadingPlans}
            >
              £ GBP
            </button>
            <button
              className={currency === 'USD' ? 'selected' : ''}
              onClick={() => setCurrency('USD')}
              disabled={loadingPlans}
            >
              $ USD
            </button>
          </div>
          <button className='close-button' onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className='modal-body'>
          {loadingPlans ? (
            <div className='plan-loading'>Loading plans...</div>
          ) : error && plans.length === 0 ? (
            <div className='plan-error'>{error}</div>
          ) : (
            <>
              <div className='plan-tabs'>
                {plans.map((plan, i) => (
                  <button
                    key={plan.priceId}
                    ref={i === activePlanIdx ? activePlanTabRef : undefined}
                    className={`plan-tab plan-${plan.name.toLowerCase()}${i === activePlanIdx ? ' selected' : ''}`}
                    onClick={() => setActivePlanIdx(i)}
                  >
                    {plan.name}
                  </button>
                ))}
              </div>
              <div className='plans-grid'>
                {plans.map((plan, planIndex) => {
                  const { heading, features } = getFeatureList(plan.name);
                  const isSubmitting = submittingPlanId === plan.priceId;
                  const planSlug = plan.name.toLowerCase();
                  const isPopular = planSlug === 'pro';
                  const isCurrent = currentPriceId === plan.priceId;

                  return (
                    <div
                      key={plan.priceId}
                      className={`plan-card plan-${planSlug}${isPopular ? ' popular' : ''}${isCurrent ? ' current' : ''}${planIndex === activePlanIdx ? ' mobile-active' : ''}`}
                    >
                      <div className='plan-card-header'>
                        {isPopular && (
                          <span className='popular-badge'>POPULAR</span>
                        )}
                        <div className='plan-name-block'>
                          <h3 className='plan-name'>{plan.name}</h3>
                          <p className='plan-tagline'>
                            {PLAN_TAGLINES[plan.name.toLowerCase()] ||
                              plan.description}
                          </p>
                        </div>
                        <p className='plan-price'>
                          <span className='price-amount'>
                            {formatPrice(plan.amount, plan.currency)}
                          </span>
                          <span className='price-period'>
                            /{plan.periodUnit.toLowerCase()}
                          </span>
                        </p>
                        <button
                          className={`choose-plan-button${isCurrent ? ' current-plan' : ''}`}
                          onClick={
                            isCurrent ? undefined : () => handleChoosePlan(plan)
                          }
                          disabled={isCurrent || submittingPlanId !== null}
                        >
                          {isCurrent ? (
                            'Your plan'
                          ) : isSubmitting ? (
                            'Loading...'
                          ) : (
                            <>
                              Choose {plan.name}
                              <ArrowRightIcon />
                            </>
                          )}
                        </button>
                      </div>

                      <div className='plan-features'>
                        <p className='features-heading'>{heading}</p>
                        {features.map((feature, i) => (
                          <div key={i} className='feature-row'>
                            <CheckIcon />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
          {error && plans.length > 0 && <p className='plan-error'>{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default PlanSelectionModal;
